import type { RouteRecordNormalized } from 'vue-router';
import type { EntitlementChecker } from '#layers/access/public';
import type { SettingsNavigationGroupId } from '#layers/settings/constants/groups';
import type { SettingsNavigationGroup, SettingsNavigationItem } from '#layers/settings/types';
import { SETTINGS_NAVIGATION_GROUPS } from '#layers/settings/constants/groups';

export interface SettingsNavigation {
	groups: SettingsNavigationGroup[];
	links: Partial<Record<SettingsNavigationGroupId, SettingsNavigationItem[]>>;
}

interface ResolveSettingsNavigationOptions {
	/** All routes to scan; the settings-flagged ones become nav entries. */
	routes: readonly RouteRecordNormalized[];
	/** Checker used to drop routes whose `isVisible` gate fails. */
	can: EntitlementChecker;
	/** Whether the current user is an org admin, for org-level gates (e.g. the workspace general page). */
	isOrganizationAdmin: boolean;
}

interface SortableNavigationItem {
	id: string;
	sort?: number;
	title: string;
}

export function resolveSettingsNavigation({ routes, can, isOrganizationAdmin }: ResolveSettingsNavigationOptions): SettingsNavigation {
	const settingsRoutes = routes.flatMap((route) => {
		if (!route.meta.settings || route.meta.settings.isVisible?.() === false || typeof route.name !== 'string') {
			return [];
		}

		// A route with an isVisible gate is dropped when it returns false; no gate = always shown.
		if (route.meta.isVisible && !route.meta.isVisible({ can, isOrganizationAdmin })) {
			return [];
		}

		return [{
			name: route.name,
			settings: route.meta.settings,
		}];
	});

	const usedGroupIds = new Set(settingsRoutes.map((route) => route.settings.group));

	const groups = settingsGroupEntries()
		.flatMap(([id, group]) => usedGroupIds.has(id) ? [{ id, ...group }] : [])
		.toSorted(compareNavigationItems);

	const linksByGroupId = new Map<SettingsNavigationGroupId, SettingsNavigationItem[]>();

	for (const route of settingsRoutes) {
		const groupId = route.settings.group;
		const groupLinks = linksByGroupId.get(groupId) ?? [];

		linksByGroupId.set(groupId, groupLinks);

		groupLinks.push({
			id: route.name,
			title: route.settings.title,
			icon: route.settings.icon,
			sort: route.settings.sort,
			experimental: route.settings.experimental,
			groupId,
			to: { name: route.name },
		});
	}

	const links = Object.fromEntries(
		[...linksByGroupId.entries()].map(([groupId, groupLinks]) => [
			groupId,
			groupLinks.toSorted(compareNavigationItems),
		]),
	);

	return { groups, links };
}

function settingsGroupEntries(): [SettingsNavigationGroupId, (typeof SETTINGS_NAVIGATION_GROUPS)[SettingsNavigationGroupId]][] {
	return Object.entries(SETTINGS_NAVIGATION_GROUPS) as [SettingsNavigationGroupId, (typeof SETTINGS_NAVIGATION_GROUPS)[SettingsNavigationGroupId]][];
}

function compareNavigationItems(a: SortableNavigationItem, b: SortableNavigationItem) {
	if (a.sort !== undefined || b.sort !== undefined) {
		const sortCompare = (a.sort ?? Number.POSITIVE_INFINITY) - (b.sort ?? Number.POSITIVE_INFINITY);

		if (sortCompare !== 0) return sortCompare;
	}

	const titleCompare = a.title.localeCompare(b.title, 'en-US');

	if (titleCompare !== 0) return titleCompare;

	return a.id.localeCompare(b.id, 'en-US');
}
